import {PageHeader} from '../ui/PageHeader';
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { api } from "../api";
import { Link } from "../ui/ScopedLink";
import { withBuildingScope } from "../ui/building-scope";
import { DateOnlyInput } from "../components/DateOnlyInput";
import {
  formatDateOnly,
  formatDateTime,
  timestampDateOnlyValue,
} from "../dates";

const root = "/api/operations/programmes",
  route = "/operations/programmes";
const empty = (buildingId: string) => ({
  id: crypto.randomUUID(),
  buildingId,
  title: "Monthly visual inspection",
  targetKind: "Building",
  targetId: buildingId,
  basis: "Monthly visual observations of accessible building areas",
  templateId: "",
  templateVersion: 1,
  bindings: {},
  instructions:
    "Inspect accessible common areas, parking, driveway and bins. Add area photographs and record concerns or areas not inspected. Specialist testing remains outsourced.",
  rule: "Calendar monthly",
  intervalDays: null,
  intervalMonths: 1,
  anchorDay: 1,
  completionPolicy: "observations-and-follow-up",
  firstDueOn: "",
  windowBeforeDays: 7,
  durationMinutes: 30,
  eligibilityMode: "No specialist requirement",
  eligibilityBasis:
    "Visual observations only; specialist testing is separately outsourced",
});
/** Desktop setup reuses the original Programme, reviewed source revisions and bounded worker. */
export function MonthlyInspectionSource({
  sourceId,
}: {
  sourceId?: string | undefined;
}) {
  const params = useParams(),
    id = sourceId ?? params.id,
    location = useLocation(),
    navigate = useNavigate(),
    scope = new URLSearchParams(location.search).get("building") ?? "";
  const [building, setBuilding] = useState(scope),
    [buildings, setBuildings] = useState<any[]>([]),
    [data, setData] = useState<any>(),
    [options, setOptions] = useState<any>(),
    [config, setConfig] = useState<any>(() => empty(scope));
  const [team, setTeam] = useState({
    lifecycle: "Draft",
    serviceActorId: "",
    coordinatorAccountId: "",
    finalReviewerAccountId: "",
    startsOn: timestampDateOnlyValue(new Date()),
    endsOn: "",
    basis: "",
  });
  const [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [pendingView, setPendingView] = useState(false),
    [review, setReview] = useState(""),
    [loaded, setLoaded] = useState(false);
  const pending = useRef<
      | { path: string; body: string; confirmed: boolean; savedId: string }
      | undefined
    >(undefined),
    running = useRef(false);
  const load = async (currentId = id) => {
    if (currentId) {
      const next = await api<any>(
        withBuildingScope(
          root + "/" + currentId + "/monthly",
          scope || undefined,
        ),
      );
      setData(next);
      setOptions({ ...next.options, writesEnabled: next.writesEnabled });
      setConfig(next.source.configuration);
      setBuilding(next.source.development_id);
      const p = next.policies[0];
      if (p)
        setTeam({
          lifecycle: p.lifecycle,
          serviceActorId: p.service_actor_id,
          coordinatorAccountId: p.coordinator_account_id,
          finalReviewerAccountId: p.final_reviewer_account_id,
          startsOn: p.starts_on,
          endsOn: p.ends_on,
          basis: p.basis,
        });
      setLoaded(true);
    }
  };
  useEffect(() => {
    setLoaded(false);
    if (id) void load().catch((e) => setError(e.message));
    else if (!scope)
      void api<any>(root + "/options")
        .then((o) => setBuildings(o.buildings))
        .catch((e) => setError(e.message));
  }, [id, scope]);
  useEffect(() => {
    if (id || !building) return;
    let active = true;
    setOptions(undefined);
    api<any>(withBuildingScope(root + "/monthly/options", building))
      .then((o) => {
        if (active) {
          setOptions(o);
          setConfig(empty(building));
          setLoaded(true);
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [id, building]);
  async function retry() {
    if (running.current || !pending.current) return;
    running.current = true;
    setBusy(true);
    setError("");
    try {
      const command = pending.current;
      if (!command.confirmed) {
        try {
          await api(command.path, { method: "POST", body: command.body });
          command.confirmed = true;
        } catch (e: any) {
          if (e.statusCode >= 400 && e.statusCode < 500) {
            pending.current = undefined;
            setPendingView(false);
          }
          throw e;
        }
      }
      await load(command.savedId);
      pending.current = undefined;
      setPendingView(false);
      setMessage(
        "Monthly inspection source saved. Retained dates and evidence remain available.",
      );
      if (!id)
        navigate(
          withBuildingScope(route + "/" + command.savedId + "/edit", building),
          { replace: true },
        );
    } catch (e: any) {
      setError(
        (pending.current?.confirmed
          ? "Saved. Reload the source to confirm its current state: "
          : "") + e.message,
      );
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  function send(path: string, body: any) {
    if (running.current || pending.current) return;
    pending.current = {
      path: withBuildingScope(path, building),
      body: JSON.stringify({ ...body, idempotencyKey: crypto.randomUUID() }),
      confirmed: false,
      savedId: config.id,
    };
    setPendingView(true);
    void retry();
  }
  const policy = data?.policies[0],
    locked =
      busy ||
      pendingView ||
      !options?.writesEnabled ||
      !options?.canConfigure ||
      policy?.lifecycle === "Ended",
    change = (patch: any) => setConfig((c: any) => ({ ...c, ...patch }));
  return (
    <div className="field-reports report-builder">
      <Link to={withBuildingScope(route, building || undefined)}>
        Work Programme
      </Link>
      <PageHeader title={id ? "Monthly inspection source" : "Set up monthly visual inspections"}/>
      <p>
        The building owns one calendar requirement each month. Operations
        selects an available, competent officer when booking attendance.
        Specialist testing stays separate.
      </p>
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
      {pendingView && !busy && (
        <button type="button" onClick={() => void retry()}>
          {pending.current?.confirmed
            ? "Reload saved source"
            : "Retry retained source change"}
        </button>
      )}
      {!id && !scope && (
        <label>
          Building
          <select
            aria-label="Building"
            required
            value={building}
            onChange={(e) => setBuilding(e.target.value)}
          >
            <option value="">Choose building</option>
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {options && (
        <>
          <p>{options.buildingName}</p>
          {!options.writesEnabled && (
            <p>
              Inspection source changes are not enabled. This setup is
              read-only.
            </p>
          )}
          {!options.canConfigure && (
            <p>
              A current Property Manager with source management authority must
              review changes.
            </p>
          )}
          {policy?.lifecycle === "Ended" && (
            <p>
              This source has ended. Its records are retained; a new separately
              reviewed source is required to restart inspections.
            </p>
          )}
        </>
      )}
      {loaded && options && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send(root + "/" + config.id + "/monthly", {
              id: crypto.randomUUID(),
              expectedVersion: data?.source.version ?? 0,
              configuration: config,
              ...team,
            });
          }}
        >
          <fieldset disabled={locked}>
            <legend>Monthly inspection</legend>
            <label>
              Title
              <input
                required
                minLength={3}
                maxLength={180}
                value={config.title}
                onChange={(e) => change({ title: e.target.value })}
              />
            </label>
            <label>
              Published inspection checklist
              <select
                aria-label="Published inspection checklist"
                required
                value={config.templateId}
                onChange={(e) => {
                  const t = options.templates.find(
                    (t: any) => t.id === e.target.value,
                  );
                  change({
                    templateId: t?.id ?? "",
                    templateVersion: t?.version ?? 1,
                  });
                }}
              >
                <option value="">Choose published method</option>
                {!options.templates.some(
                  (t: any) => t.id === config.templateId,
                ) &&
                  config.templateId && (
                    <option value={config.templateId}>
                      Retained method version {config.templateVersion}
                    </option>
                  )}
                {options.templates.map((t: any) => (
                  <option key={t.id} value={t.id}>
                    {t.title} · version {t.version}
                  </option>
                ))}
              </select>
            </label>
            <p>
              Retained method version {config.templateVersion}.{" "}
              <Link to="/admin/policies/templates">
                Review published methods
              </Link>
            </p>
            <label>
              First due date
              <DateOnlyInput
                required
                disabled={!!data?.cycles.length}
                value={config.firstDueOn}
                onChange={(v) =>
                  change({
                    firstDueOn: v ?? "",
                    anchorDay: Number(v?.slice(8) ?? 1),
                  })
                }
              />
            </label>
            <label>
              Monthly pattern
              <select aria-label="Monthly pattern" value={config.monthlyPattern ?? "Day of month"} onChange={(e) => change({monthlyPattern:e.target.value === "First weekday" ? "First weekday" : undefined,weekday:e.target.value === "First weekday" ? 1 : undefined})}>
                <option>Day of month</option>
                <option>First weekday</option>
              </select>
            </label>
            {config.monthlyPattern === "First weekday" ? <label>
              Weekday
              <select aria-label="Weekday" value={config.weekday ?? 1} onChange={(e) => change({weekday:Number(e.target.value)})}>
                {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((name,index)=><option key={name} value={index+1}>{name}</option>)}
              </select>
            </label> : <p>Monthly anchor day {config.anchorDay}; shorter months use their last day.</p>}
            <p>The first due date must match the pattern. Existing requirements keep their original dates and configuration.</p>
            <label>
              Acceptable days before due
              <input
                required
                type="number"
                min={0}
                max={366}
                value={config.windowBeforeDays}
                onChange={(e) =>
                  change({ windowBeforeDays: Number(e.target.value) })
                }
              />
            </label>
            <label>
              Expected duration in minutes
              <input
                required
                type="number"
                min={5}
                max={720}
                value={config.durationMinutes}
                onChange={(e) =>
                  change({ durationMinutes: Number(e.target.value) })
                }
              />
            </label>
            <label>
              Instructions and access notes
              <textarea
                maxLength={4000}
                value={config.instructions}
                onChange={(e) => change({ instructions: e.target.value })}
              />
            </label>
            <label>
              Source basis
              <textarea
                required
                minLength={10}
                maxLength={3000}
                value={config.basis}
                onChange={(e) => change({ basis: e.target.value })}
              />
            </label>
            <label>
              Competence requirement
              <select
                aria-label="Competence requirement"
                value={config.eligibilityMode}
                onChange={(e) => change({ eligibilityMode: e.target.value })}
              >
                <option>No specialist requirement</option>
                <option>Recorded requirements</option>
              </select>
            </label>
            <label>
              Competence basis
              <textarea
                required
                minLength={10}
                maxLength={3000}
                value={config.eligibilityBasis}
                onChange={(e) => change({ eligibilityBasis: e.target.value })}
              />
            </label>
          </fieldset>
          <fieldset disabled={locked}>
            <legend>Source state and review team</legend>
            <label>
              Source state
              <select
                aria-label="Source state"
                value={team.lifecycle}
                onChange={(e) =>
                  setTeam({ ...team, lifecycle: e.target.value })
                }
              >
                {["Draft", "Active", ...(id ? ["Paused", "Ended"] : [])].map(
                  (v) => (
                    <option key={v}>{v}</option>
                  ),
                )}
              </select>
            </label>
            <p>
              Pausing or ending retains the current configuration, attendance,
              due dates and evidence. Resuming requires current authority and
              review dates.
            </p>
            {(
              [
                ["serviceActorId", "Existing scheduling service", "services"],
                [
                  "coordinatorAccountId",
                  "Operations Coordinator",
                  "coordinators",
                ],
                [
                  "finalReviewerAccountId",
                  "Final Property Manager reviewer",
                  "finalReviewers",
                ],
              ] as const
            ).map(([key, label, list]) => (
              <label key={key}>
                {label}
                <select
                  aria-label={label}
                  required
                  value={team[key]}
                  onChange={(e) => setTeam({ ...team, [key]: e.target.value })}
                >
                  <option value="">Choose existing eligible account</option>
                  {!options[list].some((u: any) => u.id === team[key]) &&
                    team[key] && (
                      <option value={team[key]}>
                        Retained account — review current eligibility
                      </option>
                    )}
                  {options[list].map((u: any) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            <label>
              Review team start date
              <DateOnlyInput
                required
                value={team.startsOn}
                onChange={(v) => setTeam({ ...team, startsOn: v ?? "" })}
              />
            </label>
            <label>
              Review team end date (not included)
              <DateOnlyInput
                required
                value={team.endsOn}
                onChange={(v) => setTeam({ ...team, endsOn: v ?? "" })}
              />
            </label>
            <label>
              Review basis
              <textarea
                required
                minLength={10}
                maxLength={4000}
                value={team.basis}
                onChange={(e) => setTeam({ ...team, basis: e.target.value })}
              />
            </label>
            <button>Save monthly inspection source</button>
          </fieldset>
        </form>
      )}
      {data && (
        <>
          <section aria-label="Monthly generation status">
            <h2>Monthly requirements</h2>
            <p>
              {data.scan?.reconciliation_required
                ? "Historical review required."
                : policy?.lifecycle === "Active"
                  ? "Source active; generation also requires both operational write and scheduler controls."
                  : "Generation is held by the current source state."}{" "}
              {data.scan?.detail}
            </p>
            <p>
              {data.missing.count} missing monthly requirements. Generating a
              requirement does not book attendance or record an inspection.
            </p>
            {data.missing.reviewed && (
              <p>
                The oldest missing requirements are already released for bounded
                generation. No further historical review is needed for that
                batch.
              </p>
            )}
            {data.missing.count > 12 && !data.missing.reviewed && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  send(root + "/" + id + "/monthly/reconcile", {
                    id: crypto.randomUUID(),
                    policyId: policy.id,
                    expectedVersion: data.source.version,
                    periods: data.missing.periods,
                    basis: review,
                  });
                }}
              >
                <fieldset disabled={locked || policy.lifecycle !== "Active"}>
                  <legend>Review oldest missing months</legend>
                  <p>
                    Release these exact months as outstanding requirements, at
                    most two per worker pass. No historical inspection is
                    asserted and no month is skipped.
                  </p>
                  <ul>
                    {data.missing.periods.map((v: string) => (
                      <li key={v}>{formatDateOnly(v)}</li>
                    ))}
                  </ul>
                  <label>
                    Historical review and scheduling basis
                    <textarea
                      required
                      minLength={10}
                      maxLength={4000}
                      value={review}
                      onChange={(e) => setReview(e.target.value)}
                    />
                  </label>
                  <button>
                    Release oldest requirements for scheduling review
                  </button>
                </fieldset>
              </form>
            )}
            <ul>
              {data.cycles.map((c: any) => (
                <li key={c.id}>
                  <Link
                    to={withBuildingScope(
                      route + "/actions/" + c.action_id,
                      building,
                    )}
                  >
                    Due {formatDateOnly(c.due_on)}
                  </Link>{" "}
                  · {c.status}
                  {!c.configuration_current &&
                    " · Configuration changed: restore the retained method or review a separate replacement source; this requirement remains held."}
                </li>
              ))}
            </ul>
            {data.cycles.length === 100 && (
              <p>Latest 100 retained monthly requirements shown.</p>
            )}
          </section>
          <details>
            <summary>Source and review team history</summary>
            {data.policies.map((p: any) => (
              <article key={p.id}>
                <h3>
                  Revision {p.revision} · {p.lifecycle}
                </h3>
                <p>
                  {p.coordinator_name} · {p.final_reviewer_name} ·{" "}
                  {formatDateOnly(p.starts_on)} to {formatDateOnly(p.ends_on)}{" "}
                  (end not included)
                </p>
                <p>{p.basis}</p>
                <p>
                  {p.actor_name} · {formatDateTime(p.created_at)}
                </p>
              </article>
            ))}
            {data.reconciliations.map((r: any) => (
              <article key={r.id}>
                <h3>Historical requirements review</h3>
                <p>{r.periods.map(formatDateOnly).join(", ")}</p>
                <p>{r.basis}</p>
                <p>
                  {r.actor_name} · {formatDateTime(r.created_at)}
                </p>
              </article>
            ))}
            {data.policies.length === 50 && (
              <p>Latest 50 source revisions shown.</p>
            )}
            {data.reconciliations.length === 50 && (
              <p>Latest 50 historical reviews shown.</p>
            )}
          </details>
        </>
      )}
    </div>
  );
}
